import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/constants";
import { Mic, MicOff, X, Loader2, Check } from "lucide-react";

// THE CAPTURE — the front door. Free text, dictation-capable, reachable from anywhere.
// The person types/says three things in their own words: what happened, what I committed
// to, what they committed to. Everything else is DERIVED and shown for confirmation.
const MOMENTUM_LABEL = {
  advancing: "Advancing", steady: "Steady", cooling: "Cooling",
  at_risk: "At risk", stalled: "Stalled",
};

export default function CaptureButton({ presetClient, presetOpportunityId }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("write"); // write | confirm | done
  const [text, setText] = useState("");
  const [client, setClient] = useState(presetClient || "");
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [interp, setInterp] = useState(null);
  const [captureId, setCaptureId] = useState(null);
  const [listening, setListening] = useState(false);
  const [rexOpen, setRexOpen] = useState(false);
  const recogRef = useRef(null);

  // Never sit on top of the Ask Rex launcher or panel. When Rex is closed, sit just
  // above its 56px launcher; when its panel is open, step aside to the panel's left.
  useEffect(() => {
    const onPanel = (e) => setRexOpen(!!(e.detail && e.detail.open));
    window.addEventListener("rex:panel", onPanel);
    return () => window.removeEventListener("rex:panel", onPanel);
  }, []);

  useEffect(() => {
    if (!open || clients.length) return;
    fetch(`${API}/clients?limit=500`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setClients((Array.isArray(d) ? d : d.clients || d.items || []).map(
        (c) => ({ id: c.client_id, name: c.company_name || c.name })).filter((c) => c.name)))
      .catch(() => {});
  }, [open, clients.length]);

  // Item 24 — the weekly agenda (and any surface) can open the ONE capture door,
  // prefilled to a client and seeded with the question, so the meeting's outcome
  // returns through capture in the person's own words. Single authority, no second form.
  useEffect(() => {
    const onOpen = (e) => {
      const d = e.detail || {};
      reset();
      if (d.clientId) setClient(d.clientId);
      if (d.seedText) setText(d.seedText);
      setOpen(true);
    };
    window.addEventListener("rex:open-capture", onOpen);
    return () => window.removeEventListener("rex:open-capture", onOpen);
  }, []);

  // Dictation via the browser's Web Speech API (Remi works by voice).
  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Dictation is not supported in this browser — type instead."); return; }
    if (listening) { recogRef.current && recogRef.current.stop(); setListening(false); return; }
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = "en-GB";
    r.onresult = (e) => {
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      if (final) setText((t) => (t ? t + " " : "") + final.trim());
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r; r.start(); setListening(true);
  };

  const reset = () => {
    setStep("write"); setText(""); setInterp(null); setCaptureId(null);
    setError(null); setBusy(false);
    if (!presetClient) setClient("");
  };

  const submit = async () => {
    setError(null);
    if (!text.trim()) { setError("Say or type what happened."); return; }
    if (!client) { setError("Attach this to a client."); return; }
    setBusy(true);
    try {
      const cap = await fetch(`${API}/relationship/capture`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, client_reference: client, opportunity_id: presetOpportunityId || null }),
      }).then((r) => r.json());
      if (!cap.capture_id) throw new Error(cap.detail || "Capture failed");
      setCaptureId(cap.capture_id);
      const res = await fetch(`${API}/relationship/capture/${cap.capture_id}/interpret`, {
        method: "POST", credentials: "include",
      }).then((r) => r.json());
      setInterp(res.interpretation);
      setStep("confirm");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await fetch(`${API}/relationship/capture/${captureId}/confirm`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interpretation: interp }),
      }).then((r) => r.json());
      setStep("done");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const discard = async () => {
    if (captureId) {
      await fetch(`${API}/relationship/capture/${captureId}/discard`, {
        method: "POST", credentials: "include" }).catch(() => {});
    }
    reset();
  };

  const setInterpField = (path, value) => {
    setInterp((prev) => {
      const next = { ...prev };
      if (path.length === 1) next[path[0]] = value;
      else { next[path[0]] = { ...(next[path[0]] || {}), [path[1]]: value }; }
      return next;
    });
  };

  return (
    <>
      <button
        data-testid="capture-fab"
        onClick={() => { reset(); setOpen(true); }}
        className={`fixed z-40 flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:scale-105 hover:bg-slate-800 ${rexOpen ? "bottom-6 right-[27rem]" : "bottom-[5.5rem] right-6"}`}
        title="Record what happened">
        <Mic className="h-4 w-4" /> Capture
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
             onClick={() => setOpen(false)}>
          <div data-testid="capture-modal"
               className="w-full max-w-lg rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                {step === "done" ? "Recorded" : "What happened?"}
              </h2>
              <button data-testid="capture-close" onClick={() => setOpen(false)}
                      className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            {error && <div data-testid="capture-error" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

            {step === "write" && (
              <div className="space-y-3">
                <select data-testid="capture-client-select" value={client}
                        onChange={(e) => setClient(e.target.value)} disabled={!!presetClient}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option value="">Attach to a client…</option>
                  {presetClient && <option value={presetClient}>{presetClient}</option>}
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="relative">
                  <textarea data-testid="capture-text" value={text} onChange={(e) => setText(e.target.value)}
                    rows={5} autoFocus
                    placeholder="In your own words: what happened, what you committed to, what they committed to."
                    className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 pr-12 text-sm leading-relaxed" />
                  <button data-testid="capture-mic" onClick={toggleMic}
                    className={`absolute right-2 top-2 rounded-full p-2 ${listening ? "bg-red-100 text-red-600 animate-pulse" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                    title={listening ? "Stop dictation" : "Dictate"}>
                    {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                </div>
                <button data-testid="capture-submit" onClick={submit} disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading it…</> : "Read it back to me"}
                </button>
                <p className="text-xs text-slate-400">Your words are kept exactly. Rex proposes the rest for you to confirm.</p>
              </div>
            )}

            {step === "confirm" && interp && (
              <div className="space-y-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Here's what I concluded — fix anything that's wrong</p>
                <Field label="What happened">
                  <input data-testid="interp-summary" value={interp.summary || ""}
                    onChange={(e) => setInterpField(["summary"], e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                </Field>
                <Field label="Momentum">
                  <div className="flex flex-wrap gap-2" data-testid="interp-momentum">
                    {Object.keys(MOMENTUM_LABEL).map((m) => (
                      <button key={m} onClick={() => setInterpField(["momentum"], m)}
                        className={`rounded-full px-3 py-1 text-xs font-medium ${interp.momentum === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {MOMENTUM_LABEL[m]}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Our next action">
                  <input data-testid="interp-next-action" value={(interp.next_action || {}).text || ""}
                    onChange={(e) => setInterpField(["next_action", "text"], e.target.value)}
                    placeholder="—"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <CommitBox label="We committed to" testid="interp-our-commitment"
                    value={interp.our_commitment}
                    onText={(v) => setInterpField(["our_commitment"], { ...(interp.our_commitment || {}), text: v })}
                    onDate={(v) => setInterpField(["our_commitment"], { ...(interp.our_commitment || {}), due_date: v })} />
                  <CommitBox label="They committed to" testid="interp-client-commitment"
                    value={interp.client_commitment}
                    onText={(v) => setInterpField(["client_commitment"], { ...(interp.client_commitment || {}), text: v })}
                    onDate={(v) => setInterpField(["client_commitment"], { ...(interp.client_commitment || {}), due_date: v })} />
                </div>
                <div className="flex gap-2 pt-1">
                  <button data-testid="capture-confirm" onClick={confirm} disabled={busy}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm
                  </button>
                  <button data-testid="capture-discard" onClick={discard} disabled={busy}
                    className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50">Discard</button>
                </div>
              </div>
            )}

            {step === "done" && (
              <div className="space-y-4 py-4 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                  <Check className="h-6 w-6 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-600">Recorded and confirmed. The relationship, the commitments and the list are updated.</p>
                <div className="flex gap-2">
                  <button data-testid="capture-another" onClick={reset}
                    className="flex-1 rounded-lg border border-slate-300 py-2 text-sm text-slate-700 hover:bg-slate-50">Capture another</button>
                  <button onClick={() => setOpen(false)}
                    className="flex-1 rounded-lg bg-slate-900 py-2 text-sm text-white hover:bg-slate-800">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-slate-500">{label}</label>
    {children}
  </div>
);

const CommitBox = ({ label, value, onText, onDate, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={testid}>
    <div className="mb-1 text-xs font-medium text-slate-500">{label}</div>
    <input value={(value || {}).text || ""} onChange={(e) => onText(e.target.value)}
      placeholder="Nothing — leave empty"
      className="mb-2 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
    <input type="date" value={(value || {}).due_date || ""} onChange={(e) => onDate(e.target.value)}
      className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-600" />
  </div>
);
